import {
  AugmentedCreatePresentationPageInput,
  PresentationPageKind,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { ClientPresentationV2BalanceSheetBundleConfiguration } from '../types/ClientPresentation.PresentationBundleType';
import { ClientPresentationV2Page } from '../types/ClientPresentation.PresentationPageType';

/**
 * Strips the local `id` (sentinel or stale server ID) and normalizes fields
 * for the create mutation input. The server derives `columnOrder` from the
 * config state, so it's always reset to `[]` on recreate.
 */
function toBalanceSheetConfigCreateInput(
  config: ClientPresentationV2BalanceSheetBundleConfiguration
) {
  const { id: _configId, displayName, hiddenColumns, ...configFields } = config;
  return {
    ...configFields,
    displayName: displayName || undefined,
    columnOrder: [],
    hiddenColumns: hiddenColumns ?? [],
  };
}

/** Builds the create payload for one page and its kind-specific child. */
export function mapPageToCreateInput(
  page: ClientPresentationV2Page,
  householdID: string,
  index: number
): AugmentedCreatePresentationPageInput {
  return {
    create: {
      kind: page.type,
      householdID,
      index,
    },
    withCustomPage: page.customPage
      ? {
          create: {
            pageName: page.customPage.pageName,
            pageContent: page.customPage.pageContent ?? '',
            customPrompt: page.customPage.customPrompt ?? undefined,
            householdID,
          },
        }
      : undefined,
    withDiagramPage:
      page.type === PresentationPageKind.DiagramPage && page.diagramPage
        ? {
            create: {
              pageName: page.diagramPage.pageName,
              nodeConfigurations: page.diagramPage.nodeConfigurations,
              edgeDefinitions: page.diagramPage.edgeDefinitions,
              phases: page.diagramPage.phases,
              householdID,
            },
          }
        : undefined,
    withCoverPage:
      page.type === PresentationPageKind.CoverPage
        ? {
            create: {
              customContent: page.coverPage?.customContent ?? '',
              customPrompt: page.coverPage?.customPrompt ?? undefined,
              displayTitle: page.coverPage?.displayTitle ?? '',
              householdID,
            },
          }
        : undefined,
    withWaterfallBundleCoverPage:
      page.type === PresentationPageKind.WaterfallCoverPage
        ? {
            create: {
              householdID,
              content: page.coverPage?.customContent ?? '',
            },
          }
        : undefined,
    withPresentationBundleCoverPage:
      page.type === PresentationPageKind.PresentationBundleCoverPage ||
      page.type === PresentationPageKind.WaterfallComparisonCoverPage
        ? {
            create: {
              content: page.presentationBundleCoverPage?.content ?? '',
              householdID,
            },
          }
        : undefined,
    withDocumentSummaryPage:
      page.type === PresentationPageKind.DocumentSummaryPage
        ? {
            create: {
              householdID,
              summaryID: page.documentSummaryPage?.summary?.id ?? null,
              documentType: page.documentSummaryPage?.documentType ?? null,
              relevantClientProfileIDs: getNodes(
                page.documentSummaryPage?.relevantClientProfiles
              ).map((profile) => profile.id),
            },
          }
        : undefined,
    withActiveEstateInsightsPage:
      page.type === PresentationPageKind.ActiveEstateInsightsPage
        ? { create: { householdID } }
        : undefined,
    withCompletedEstateInsightsPage:
      page.type === PresentationPageKind.CompletedEstateInsightsPage
        ? { create: { householdID } }
        : undefined,
    withMissingDocumentsPage:
      page.type === PresentationPageKind.MissingDocumentsPage
        ? { create: { householdID } }
        : undefined,
    withBeneficiaryPage:
      page.type === PresentationPageKind.BeneficiaryReportBeneficiary &&
      page.beneficiaryPage?.beneficiaryID
        ? {
            create: {
              householdID,
              beneficiaryID: page.beneficiaryPage.beneficiaryID,
            },
          }
        : undefined,
    withBalanceSheetBundleConfiguration:
      (page.type === PresentationPageKind.BalanceSheetPage ||
        page.type === PresentationPageKind.BalanceSheetExportPage) &&
      page.balanceSheetBundleConfiguration
        ? {
            create: {
              householdID,
              ...toBalanceSheetConfigCreateInput(
                page.balanceSheetBundleConfiguration
              ),
            },
          }
        : undefined,
  };
}
